import { useEffect, useState, useRef } from "react";
import {
  FiGlobe,
  FiMail,
  FiPhone,
  FiChevronDown,
  FiChevronUp,
  FiUser,
} from "react-icons/fi";
import { IoBusinessOutline } from "react-icons/io5";
import { BsClock } from "react-icons/bs";
import { IProfile } from "@/interfaces/Iprofile";

const convertMinutesToTime = (minutes: number): string => {
  if (minutes === 1440) return "11:59 PM";
  const hours = Math.floor(minutes / 60);
  const mins = minutes % 60;
  const period = hours >= 12 ? "PM" : "AM";
  const displayHours = hours % 12 || 12;
  return `${displayHours}:${mins.toString().padStart(2, "0")} ${period}`;
};

const formatShifts = (shifts: any[]): string => {
  if (!shifts || shifts.length === 0) return "Closed";
  return shifts
    .map(
      (shift) =>
        `${convertMinutesToTime(shift.start)} - ${convertMinutesToTime(
          shift.end
        )}`
    )
    .join("\n");
};

const getDaySchedule = (workingHours: any, day: string): string => {
  const dayData = workingHours[day.toLowerCase()];
  if (!dayData) return "Closed";
  return formatShifts(dayData.shifts || []);
};

const getTodaySchedule = (
  workingHours: any
): { day: string; schedule: string } => {
  const days = ["su", "mo", "tu", "we", "th", "fr", "sa"];
  const dayNames = [
    "Sunday",
    "Monday",
    "Tuesday",
    "Wednesday",
    "Thursday",
    "Friday",
    "Saturday",
  ];
  const today = new Date().getDay();
  return {
    day: dayNames[today],
    schedule: getDaySchedule(workingHours, days[today]),
  };
};

const AboutTab = ({ profileData }: { profileData: IProfile }) => {
  if (!profileData) {
    return null;
  }

  console.log("profileData", profileData);
  const [isHoursOpen, setIsHoursOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setIsHoursOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  return (
    <div className="space-y-4">
      {profileData.first_name && profileData.last_name && (
        <div className="flex items-center space-x-3">
          <FiUser className="text-gray-400" />
          <span>
            {profileData.first_name} {profileData.last_name}
          </span>
        </div>
      )}

      {profileData?.phones && profileData.phones.length > 0 && (
        <div className="flex items-center space-x-3">
          <FiPhone className="text-gray-400" />
          <div className="flex flex-col">
            {profileData.phones.map((phone: string | null, index: number) => (
              <span key={index}>
                {phone}
                <br />
              </span>
            ))}
          </div>
        </div>
      )}

      {profileData?.other_emails && profileData.other_emails.length > 0 && (
        <div className="flex items-center space-x-3">
          <FiMail className="text-gray-400" />
          <div className="flex flex-col">
            {profileData.other_emails.map(
              (email: string | null, index: number) => (
                <span key={index}>{email}</span>
              )
            )}
          </div>
        </div>
      )}

      {profileData?.role && (
        <div className="flex items-center space-x-3">
          <IoBusinessOutline className="text-gray-400" />
          <span>{profileData.role}</span>
        </div>
      )}

      {profileData?.working_hours && (
        <div className="relative" ref={dropdownRef}>
          <div
            className="flex items-center space-x-3 cursor-pointer bg-gray-50 hover:bg-gray-200 p-2 rounded-lg transition-colors w-1/3"
            onClick={() => setIsHoursOpen(!isHoursOpen)}
          >
            <BsClock className="text-gray-400" />
            <div className="flex-grow">
              <span className="font-medium flex gap-2">
                {getTodaySchedule(profileData.working_hours).day}:
                {getTodaySchedule(profileData.working_hours)
                  .schedule.split("\n")
                  .map((shift, index) => (
                    <div key={index}>{shift}</div>
                  ))}
              </span>
            </div>
            {isHoursOpen ? (
              <FiChevronUp className="text-gray-400" />
            ) : (
              <FiChevronDown className="text-gray-400" />
            )}
          </div>

          {isHoursOpen && (
            <div className="absolute z-10 mt-2 w-2/3 bg-white rounded-lg shadow-lg border border-gray-200 p-4">
              <div className="space-y-2">
                <div className="grid grid-cols-2 gap-2">
                  <div className="text-gray-600">Monday:</div>
                  <div>
                    {getDaySchedule(profileData.working_hours, "mo")
                      .split("\n")
                      .map((shift, index) => (
                        <div key={index}>{shift}</div>
                      ))}
                  </div>
                  <div className="text-gray-600">Tuesday:</div>
                  <div>
                    {getDaySchedule(profileData.working_hours, "tu")
                      .split("\n")
                      .map((shift, index) => (
                        <div key={index}>{shift}</div>
                      ))}
                  </div>
                  <div className="text-gray-600">Wednesday:</div>
                  <div>
                    {getDaySchedule(profileData.working_hours, "we")
                      .split("\n")
                      .map((shift, index) => (
                        <div key={index}>{shift}</div>
                      ))}
                  </div>
                  <div className="text-gray-600">Thursday:</div>
                  <div>
                    {getDaySchedule(profileData.working_hours, "th")
                      .split("\n")
                      .map((shift, index) => (
                        <div key={index}>{shift}</div>
                      ))}
                  </div>
                  <div className="text-gray-600">Friday:</div>
                  <div>
                    {getDaySchedule(profileData.working_hours, "fr")
                      .split("\n")
                      .map((shift, index) => (
                        <div key={index}>{shift}</div>
                      ))}
                  </div>
                  <div className="text-gray-600">Saturday:</div>
                  <div>
                    {getDaySchedule(profileData.working_hours, "sa")
                      .split("\n")
                      .map((shift, index) => (
                        <div key={index}>{shift}</div>
                      ))}
                  </div>
                  <div className="text-gray-600">Sunday:</div>
                  <div>
                    {getDaySchedule(profileData.working_hours, "su")
                      .split("\n")
                      .map((shift, index) => (
                        <div key={index}>{shift}</div>
                      ))}
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {profileData?.about && (
        <div className="flex items-center space-x-3">
          <IoBusinessOutline className="text-gray-400" />
          <span>{profileData.about}</span>
        </div>
      )}

      {profileData?.social_links?.website && (
        <div className="flex items-center space-x-3">
          <FiGlobe className="text-gray-400" />
          <a
            href={profileData.social_links.website}
            className="text-[#78C7D1]"
            target="_blank"
            rel="noopener noreferrer"
          >
            {profileData.social_links.website}
          </a>
        </div>
      )}
    </div>
  );
};

export default AboutTab;
